// Staff controls for the phone app and cloud reading (xReceived staff only; console prototype
// prototype/console/src/74-staffctl.js, approved by Bilal 1 Nov 2026). Super admins change them; other staff see the values.
// Server: app_version_info / staff_set_app_versions (20261101000100_app_versions.sql), staff_integrity /
// staff_set_integrity_mode (20261031000300_integrity.sql), staff_ai_settings / staff_set_cheque_ai (20261031000100_cheque_ai_caps.sql).
// Every change is written to the staff audit by the server.
import { useCallback, useEffect, useState } from 'react';
import { plain } from '../lib/errors';
import { L, prefs } from '../lib/prefs';
import { useApp } from '../lib/session';
import { DbError, rpc } from '../lib/supabase';
import { Btn, Card, Empty, Field, Ic, Meter, Note, PageHead, Switch } from '../ui/kit';
import { closeModal, confirmBox, openModal } from '../ui/modal';
import { SRow, SetSection } from '../ui/settings';
import { Pill } from '../ui/table';
import { toast } from '../ui/toast';

const nf = (n: number) => new Intl.NumberFormat('en-US').format(n);

/** Loads one staff function; null while loading, 'failed' when it couldn't. */
function useStaff<T>(fn: string, args?: Record<string, unknown>) {
  const [v, setV] = useState<T | null | 'failed'>(null);
  const key = JSON.stringify(args || {});
  const reload = useCallback(async () => {
    try { setV(await rpc<T>(fn, JSON.parse(key))); } catch { setV('failed'); }
  }, [fn, key]);
  useEffect(() => { void reload(); }, [reload]);
  return [v, reload] as const;
}

function Failed({ title, retry }: { title: string; retry: () => void }) {
  return <><PageHead title={title} /><Card><Empty icon="alert" title={L('Couldn\'t load this page', 'تعذّر تحميل هذه الصفحة')} action={<Btn onClick={retry}>{L('Try again', 'إعادة المحاولة')}</Btn>} /></Card></>;
}

/* ---------------- App versions ---------------- */
export type Platform = 'android' | 'ios';
export type Versions = { min: number; latest: number; latest_name: string | null; notes: { en: string[]; ar: string[] } };
const PLATFORM: Record<Platform, [string, string]> = { android: ['Android', 'أندرويد'], ios: ['iPhone', 'آيفون'] };

/** What a phone on this versionCode sees (the same rule as app_version_info). */
export function phoneSees(v: Pick<Versions, 'min' | 'latest'>, code: number): 'required' | 'optional' | 'ok' {
  if (code > 0 && code < v.min) return 'required';
  if (code > 0 && code < v.latest) return 'optional';
  return 'ok';
}
const SEES = {
  required: () => [L('Update required', 'التحديث مطلوب'), 'bad', L('Can\'t make new receipts until it updates. Its saved receipts still sync.', 'لا يمكنه إنشاء سندات جديدة حتى يتحدث. تتزامن سنداته المحفوظة.')],
  optional: () => [L('Update me!', 'حدّثني!'), 'warn', L('Works as normal, with an update button on Home.', 'يعمل كالمعتاد، مع زر تحديث في الرئيسية.')],
  ok: () => [L('Up to date', 'محدّث'), 'good', L('Nothing to show.', 'لا شيء يظهر.')],
} as const;

/** The version ranges a staff member reads on the card, newest rule last. */
export function seesRows(v: Pick<Versions, 'min' | 'latest'>): ['required' | 'optional' | 'ok', string][] {
  const r: ['required' | 'optional' | 'ok', string][] = [];
  if (v.min) r.push(['required', L(`Below ${v.min}`, `أقل من ${v.min}`)]);
  if (v.latest > v.min) r.push(['optional', L(`${v.min || 1} to ${v.latest - 1}`, `${v.min || 1} إلى ${v.latest - 1}`)]);
  r.push(['ok', v.latest ? L(`${v.latest} and newer`, `${v.latest} وأحدث`) : L('Every version', 'كل الإصدارات')]);
  return r;
}

/** The release-notes JSON from `node apps/mobile/scripts/release-notes.mjs --app`, or null. */
export function parseNotes(s: string): { en: string[]; ar: string[] } | null {
  try {
    const j = JSON.parse(s) as { en?: unknown; ar?: unknown };
    if (!j || !Array.isArray(j.en) || !Array.isArray(j.ar)) return null;
    return { en: j.en.map(String), ar: j.ar.map(String) };
  } catch { return null; }
}
const lines = (s: string) => s.split('\n').map(x => x.trim()).filter(Boolean);
const asInt = (s: string) => (/^\d+$/.test(s.trim()) ? Number(s.trim()) : NaN);

function VersionsForm({ p, v, onSaved }: { p: Platform; v: Versions; onSaved: () => void }) {
  const [latest, setLatest] = useState(String(v.latest));
  const [name, setName] = useState(v.latest_name || '');
  const [min, setMin] = useState(String(v.min));
  const [en, setEn] = useState(v.notes.en.join('\n'));
  const [ar, setAr] = useState(v.notes.ar.join('\n'));
  const [paste, setPaste] = useState('');
  const [pasteErr, setPasteErr] = useState(false);
  const [busy, setBusy] = useState(false);
  const lt = asInt(latest), mn = asInt(min);
  const err = !Number.isInteger(lt) || !Number.isInteger(mn) || lt > 2_100_000_000 || mn > 2_100_000_000 ? L('Version codes are whole numbers, 0 or more.', 'أرقام الإصدار أعداد صحيحة، 0 أو أكثر.')
    : mn > lt ? L('The oldest allowed can\'t be newer than the latest.', 'لا يمكن أن يكون الأقدم المسموح أحدث من الأحدث.') : '';
  const fill = () => {
    const n = parseNotes(paste);
    if (!n) { setPasteErr(true); return; }
    setEn(n.en.join('\n')); setAr(n.ar.join('\n')); setPaste(''); setPasteErr(false);
  };
  const save = async () => {
    if (err) return;
    setBusy(true);
    try {
      await rpc('staff_set_app_versions', { p_platform: p, p_min: mn, p_latest: lt, p_latest_name: name.trim() || null, p_notes: { en: lines(en), ar: lines(ar) } });
      closeModal();
      toast(L('Saved. Phones see it at their next check.', 'تم. تراه الهواتف عند الفحص التالي.'));
      onSaved();
    } catch (e) {
      toast(e instanceof DbError && e.code === 'versions_invalid' ? L('The oldest allowed can\'t be newer than the latest.', 'لا يمكن أن يكون الأقدم المسموح أحدث من الأحدث.') : plain(e), { tone: 'bad' });
      setBusy(false);
    }
  };
  return (
    <>
      <div className="m-body">
        <div className="form-2">
          <Field label={L('Latest versionCode on the store', 'أحدث رقم إصدار في المتجر')} htmlFor="av-l" hint={L('Older phones see "Update me!"', 'الهواتف الأقدم ترى «حدّثني!»')}>
            <input className="inp" id="av-l" inputMode="numeric" dir="ltr" value={latest} onChange={e => setLatest(e.target.value)} data-autofocus />
          </Field>
          <Field label={L('Its version name', 'اسم الإصدار')} htmlFor="av-n">
            <input className="inp" id="av-n" dir="ltr" placeholder="0.6.440120" value={name} onChange={e => setName(e.target.value)} />
          </Field>
          <Field label={L('Oldest versionCode that can make receipts', 'أقدم رقم إصدار يصدر سندات')} htmlFor="av-m" hint={L('0 = no limit', '0 = بلا حد')} err={err || undefined}>
            <input className="inp" id="av-m" inputMode="numeric" dir="ltr" value={min} onChange={e => setMin(e.target.value)} />
          </Field>
        </div>
        <Field label={L('What\'s new (English), one per line', 'ما الجديد (إنجليزي)، سطر لكل بند')} htmlFor="av-en">
          <textarea className="inp" id="av-en" rows={4} dir="ltr" value={en} onChange={e => setEn(e.target.value)} />
        </Field>
        <Field label={L('What\'s new (Arabic), one per line', 'ما الجديد (عربي)، سطر لكل بند')} htmlFor="av-ar">
          <textarea className="inp" id="av-ar" rows={4} dir="rtl" value={ar} onChange={e => setAr(e.target.value)} />
        </Field>
        <div className="av-imp">
          <b>{L('Fill from release notes', 'املأ من ملاحظات الإصدار')}</b>
          <p className="muted sm">{L('Paste the output of', 'الصق ناتج')} <bdi><code dir="ltr">node apps/mobile/scripts/release-notes.mjs --app</code></bdi>.</p>
          <textarea className="inp" id="av-paste" rows={3} dir="ltr" placeholder='{"en":[…],"ar":[…]}' aria-label={L('Release notes JSON', 'JSON ملاحظات الإصدار')} value={paste} onChange={e => { setPaste(e.target.value); setPasteErr(false); }} />
          {pasteErr ? <p className="err"><Ic n="alert" />{L('That isn\'t the release-notes JSON: {"en": [...], "ar": [...]}.', 'هذا ليس JSON ملاحظات الإصدار: {"en": [...], "ar": [...]}.')}</p> : null}
          <Btn sm kind="ghost" disabled={!paste.trim()} onClick={fill}>{L('Fill the notes', 'املأ الملاحظات')}</Btn>
        </div>
        {!err && mn > v.min ? <Note tone="warn">{L(`Phones older than ${mn} will stop making new receipts at their next check, until they update.`, `ستتوقف الهواتف الأقدم من ${mn} عن إنشاء سندات جديدة حتى تتحدث.`)}</Note> : null}
      </div>
      <footer className="m-foot">
        <Btn kind="ghost" onClick={closeModal}>{L('Cancel', 'إلغاء')}</Btn>
        <Btn kind="primary" disabled={!!err} busy={busy} onClick={() => void save()}>{L('Save', 'حفظ')}</Btn>
      </footer>
    </>
  );
}

function PlatformCard({ p, v, canChange, onSaved }: { p: Platform; v: Versions; canChange: boolean; onSaved: () => void }) {
  const lang = prefs.get().lang === 'ar' ? 'ar' : 'en';
  const notes = v.notes?.[lang] || [];
  const edit = () => openModal({ title: L(PLATFORM[p][0], PLATFORM[p][1]) + L(': app versions', ': إصدارات التطبيق'), size: 'md', bare: true, body: <VersionsForm p={p} v={v} onSaved={onSaved} /> });
  return (
    <Card cls="span-6 av-card" id={'av-' + p} title={<><Ic n="phone" /> {L(PLATFORM[p][0], PLATFORM[p][1])}</>}>
      <dl className="kv">
        <div><dt>{L('Latest on the store', 'الأحدث في المتجر')}</dt>
        <dd>{v.latest ? <><span className="mono">{v.latest}</span>{v.latest_name ? <small className="sub">{v.latest_name}</small> : null}</> : <span className="muted">{L('Not set: no update offered', 'غير محدد: لا يُعرض تحديث')}</span>}</dd></div>
        <div><dt>{L('Oldest that can make receipts', 'أقدم إصدار يصدر سندات')}</dt>
        <dd>{v.min ? <span className="mono">{v.min}</span> : <span className="muted">{L('No limit', 'بلا حد')}</span>}</dd></div>
      </dl>
      <h3 className="d-h">{L('What\'s new', 'ما الجديد')}</h3>
      {notes.length ? <ul className="av-notes">{notes.map((n, i) => <li key={i}>{n}</li>)}</ul> : <p className="muted sm">{L('No notes yet.', 'لا ملاحظات بعد.')}</p>}
      <h3 className="d-h">{L('What phones see', 'ما تراه الهواتف')}</h3>
      <ul className="av-sees">
        {seesRows(v).map(([k, range]) => { const [label, tone, help] = SEES[k](); return <li key={k}><Pill tone={tone}>{label}</Pill><span><b>{range}</b><small>{help}</small></span></li>; })}
      </ul>
      <div className="av-act"><Btn kind="ghost" sm icon="edit" disabled={!canChange} why={L('Only super admins change app versions.', 'فقط المسؤول الأعلى يغير إصدارات التطبيق.')} onClick={edit}>{L('Edit', 'تعديل')}</Btn></div>
    </Card>
  );
}

export function AppVersionsPage() {
  const { staff } = useApp();
  const [a, ra] = useStaff<Versions>('app_version_info', { p_platform: 'android', p_code: 0 });
  const [i, ri] = useStaff<Versions>('app_version_info', { p_platform: 'ios', p_code: 0 });
  if (!staff) return null;
  const title = L('App versions', 'إصدارات التطبيق');
  if (a === 'failed' || i === 'failed') return <Failed title={title} retry={() => { void ra(); void ri(); }} />;
  if (!a || !i) return <div className="boot" aria-busy="true" />;
  const canChange = staff === 'super';
  return (
    <>
      <PageHead title={title} sub={L('The newest app on each store and the oldest that can still make receipts. Phones check when they open and at least once a day.', 'أحدث تطبيق في كل متجر وأقدم إصدار يصدر سندات. تفحص الهواتف عند الفتح ومرة يومياً على الأقل.')} />
      {!canChange ? <Note icon="lock">{L('Only super admins change app versions.', 'فقط المسؤول الأعلى يغير إصدارات التطبيق.')}</Note> : null}
      <div className="grid g-12">
        <PlatformCard p="android" v={a} canChange={canChange} onSaved={() => void ra()} />
        <PlatformCard p="ios" v={i} canChange={canChange} onSaved={() => void ri()} />
      </div>
    </>
  );
}

/* ---------------- Play Integrity ---------------- */
export type Mode = 'off' | 'log' | 'enforce';
type Integrity = { mode: Mode; checked_today: number; passed_today: number; can_change: boolean };
const MODES: [Mode, () => string, () => string][] = [
  ['off', () => L('Off', 'متوقف'), () => L('Phones are not checked.', 'لا تُفحص الهواتف.')],
  ['log', () => L('Log only', 'تسجيل فقط'), () => L('Phones are checked and the result is kept. Nothing is blocked.', 'تُفحص الهواتف ويُحفظ الناتج. لا يُمنع شيء.')],
  ['enforce', () => L('Enforce', 'إلزام'), () => L('A phone that hasn\'t passed in the last 24 hours can\'t make new receipts. Its saved receipts still sync.', 'الهاتف الذي لم ينجح خلال 24 ساعة لا يصدر سندات جديدة. تتزامن سنداته المحفوظة.')],
];

export function IntegrityPage() {
  const { staff } = useApp();
  const [g, reload] = useStaff<Integrity>('staff_integrity');
  const [busy, setBusy] = useState(false);
  if (!staff) return null;
  const title = L('Play Integrity', 'Play Integrity');
  if (g === 'failed') return <Failed title={title} retry={() => void reload()} />;
  if (!g) return <div className="boot" aria-busy="true" />;
  const failed = Math.max(0, g.checked_today - g.passed_today);
  const set = async (k: Mode) => {
    setBusy(true);
    try {
      await rpc('staff_set_integrity_mode', { p_mode: k });
      toast(L('Saved. Phones follow it at their next check.', 'تم. تتبعه الهواتف عند الفحص التالي.'));
      await reload();
    } catch (e) { toast(plain(e), { tone: 'bad' }); }
    setBusy(false);
  };
  const pick = (k: Mode) => {
    if (!g.can_change || busy || k === g.mode) return;
    if (k === 'enforce') {
      confirmBox({
        title: L('Enforce Play Integrity?', 'إلزام Play Integrity؟'), danger: true, ok: L('Enforce now', 'ألزم الآن'), onOk: () => void set(k),
        body: L(`${nf(failed)} phones failed in the last 24 hours. From now on they can't make new receipts until they pass, and staff can't override it per company. Use it only after the checks look right in Log only.`, `${nf(failed)} هاتفاً فشل خلال 24 ساعة. لن تصدر سندات جديدة حتى تنجح. استخدمه فقط بعد التأكد في وضع التسجيل.`),
      });
      return;
    }
    void set(k);
  };
  return (
    <>
      <PageHead title={title} sub={L('Google checks that a phone runs our real app from the Play Store on a genuine device.', 'تتحقق Google من أن الهاتف يشغل تطبيقنا الحقيقي من متجر Play على جهاز أصلي.')} />
      <div className="mini-kpis int-kpis">
        <div><span>{L('Phones checked, last 24 h', 'هواتف فُحصت، آخر 24 ساعة')}</span><b>{nf(g.checked_today)}</b></div>
        <div><span>{L('Passed', 'نجحت')}</span><b>{nf(g.passed_today)}</b><small>{g.checked_today ? (g.passed_today / g.checked_today * 100).toFixed(1) + '%' : '—'}</small></div>
        <div className={failed ? 'warn' : ''}><span>{L('Failed', 'فشلت')}</span><b>{nf(failed)}</b><small>{L('would be blocked when enforced', 'ستُمنع عند الإلزام')}</small></div>
      </div>
      <Card title={L('Play Integrity check', 'فحص Play Integrity')}>
        <div className="int-modes" role="radiogroup" aria-label={L('Play Integrity mode', 'وضع Play Integrity')}>
          {MODES.map(([k, t, d]) => (
            <button key={k} type="button" role="radio" aria-checked={g.mode === k} className={['mdl', g.mode === k && 'on', k === 'enforce' && 'enf'].filter(Boolean).join(' ')}
              aria-disabled={!g.can_change || busy ? 'true' : undefined} onClick={() => pick(k)}>
              <span className="dotr" /><span className="mdl-b"><b>{t()}</b><small>{d()}</small></span>
            </button>
          ))}
        </div>
        {!g.can_change ? <Note icon="lock">{L('Only super admins change the mode.', 'فقط المسؤول الأعلى يغير الوضع.')}</Note> : null}
      </Card>
    </>
  );
}

/* ---------------- Cloud reading switch and daily caps ---------------- */
type Guards = { enabled: boolean; daily_cap: number; device_daily_cap: number; used_today: number };
type AiSettings = { cheque_ai: Guards; cheque_model: string | null; can_change: boolean };

export function capsErr(daily: number, device: number): string {
  if (!(Number.isInteger(daily) && daily >= 0 && daily <= 100000)) return L('All companies: a whole number from 0 to 100,000.', 'كل الشركات: رقم صحيح من 0 إلى 100,000.');
  if (!(Number.isInteger(device) && device >= 0 && device <= 1000)) return L('Per phone: a whole number from 0 to 1,000.', 'لكل هاتف: رقم صحيح من 0 إلى 1,000.');
  return '';
}

function CapsForm({ c, onSaved }: { c: Guards; onSaved: () => void }) {
  const [daily, setDaily] = useState(String(c.daily_cap));
  const [device, setDevice] = useState(String(c.device_daily_cap));
  const [busy, setBusy] = useState(false);
  const err = capsErr(asInt(daily), asInt(device));
  const save = async () => {
    if (err) return;
    setBusy(true);
    try {
      await rpc('staff_set_cheque_ai', { p_daily_cap: asInt(daily), p_device_daily_cap: asInt(device) });
      closeModal(); toast(L('Saved', 'تم الحفظ')); onSaved();
    } catch (e) { toast(plain(e), { tone: 'bad' }); setBusy(false); }
  };
  return (
    <>
      <div className="m-body">
        <Field label={L('All companies, per day', 'كل الشركات، يومياً')} htmlFor="cai-d">
          <input className="inp" id="cai-d" inputMode="numeric" dir="ltr" value={daily} onChange={e => setDaily(e.target.value)} data-autofocus />
        </Field>
        <Field label={L('Each phone, per day', 'كل هاتف، يومياً')} htmlFor="cai-p" err={err || undefined}>
          <input className="inp" id="cai-p" inputMode="numeric" dir="ltr" value={device} onChange={e => setDevice(e.target.value)} />
        </Field>
      </div>
      <footer className="m-foot">
        <Btn kind="ghost" onClick={closeModal}>{L('Cancel', 'إلغاء')}</Btn>
        <Btn kind="primary" disabled={!!err} busy={busy} onClick={() => void save()}>{L('Save', 'حفظ')}</Btn>
      </footer>
    </>
  );
}

export function CloudReadingPage() {
  const { staff } = useApp();
  const [s, reload] = useStaff<AiSettings>('staff_ai_settings');
  if (!staff) return null;
  const title = L('Cloud reading', 'القراءة السحابية');
  if (s === 'failed') return <Failed title={title} retry={() => void reload()} />;
  if (!s) return <div className="boot" aria-busy="true" />;
  const c = s.cheque_ai, ro = !s.can_change;
  const why = L('Your staff role can\'t do this', 'دورك لا يسمح بذلك');
  const setOn = async (on: boolean) => {
    try { await rpc('staff_set_cheque_ai', { p_enabled: on }); toast(L('Saved', 'تم الحفظ')); await reload(); } catch (e) { toast(plain(e), { tone: 'bad' }); }
  };
  const toggle = () => {
    if (ro) return;
    if (c.enabled) confirmBox({ title: L('Turn cloud reading off for everyone?', 'إيقاف القراءة السحابية للجميع؟'), body: L('Phones read cheques themselves until it is back on. Nothing else stops.', 'تقرأ الهواتف الشيكات بنفسها حتى تعود. لا يتوقف شيء آخر.'), ok: L('Turn off', 'إيقاف'), danger: true, onOk: () => void setOn(false) });
    else void setOn(true);
  };
  const edit = () => openModal({ title: L('Daily caps for cloud reading', 'الحدود اليومية للقراءة السحابية'), size: 'sm', bare: true, body: <CapsForm c={c} onSaved={() => void reload()} /> });
  return (
    <>
      <PageHead title={title} sub={L('The AI that reads cheque photos for the phone app. Each plan\'s reads are set in the console\'s Plans & limits.', 'الذكاء الذي يقرأ صور الشيكات لتطبيق الهاتف. قراءات كل خطة تُضبط في الخطط والحدود.')} />
      {ro ? <Note icon="lock">{L('Only super admins change the switch and the caps.', 'فقط المسؤول الأعلى يغير المفتاح والحدود.')}</Note> : null}
      <SetSection id="aiguard" icon="shield" title={L('Cloud reading switch and daily caps', 'مفتاح القراءة السحابية والحدود اليومية')}
        sub={L('A safety net on top of the plan limits. When it is off, or a cap is reached, phones read cheques themselves; nothing is blocked. The day starts at midnight in Dubai.', 'شبكة أمان فوق حدود الخطط. عند الإيقاف أو بلوغ الحد، تقرأ الهواتف الشيكات بنفسها ولا يتوقف شيء. يبدأ اليوم منتصف الليل بتوقيت دبي.')}>
        <SRow label={L('Cloud reading', 'القراءة السحابية')} help={c.enabled ? L('On for every company', 'مفعلة لكل الشركات') : L('Off for everyone', 'متوقفة للجميع')}>
          <Switch id="cai-on" on={c.enabled} onToggle={toggle} disabled={ro} why={why} label={L('Cloud reading', 'القراءة السحابية')} />
        </SRow>
        <SRow label={L('Today, all companies', 'اليوم، كل الشركات')} help={L(`${nf(c.used_today)} of ${nf(c.daily_cap)} reads`, `${nf(c.used_today)} من ${nf(c.daily_cap)} قراءة`)}>
          <div className="cai-meter"><Meter v={c.used_today} max={c.daily_cap} /></div>
        </SRow>
        <SRow label={L('Daily cap, all companies', 'الحد اليومي لكل الشركات')} help={L('0 stops cloud reading for the rest of the day', '0 يوقف القراءة السحابية لبقية اليوم')}>
          <b className="mono">{nf(c.daily_cap)}</b>
        </SRow>
        <SRow label={L('Daily cap per phone', 'الحد اليومي لكل هاتف')} help={L('Stops one phone from using up the day', 'يمنع هاتفاً واحداً من استهلاك اليوم')}>
          <b className="mono">{nf(c.device_daily_cap)}</b>
        </SRow>
        <div className="av-act"><Btn kind="ghost" sm icon="edit" disabled={ro} why={why} onClick={edit}>{L('Change caps', 'تغيير الحدود')}</Btn></div>
      </SetSection>
    </>
  );
}
